<!-- Dashboard Banners Component - shared between Home.vue and Parameters.vue -->
<template>
  <div>
    <!-- Display mode: used on the dashboard -->
    <div
      v-if="mode === 'display' && activeBanners.length"
      class="dashboard-banners"
    >
      <div
        v-for="banner in activeBanners"
        :key="banner.id"
        class="dashboard-banner"
        :style="{ backgroundColor: banner.background_color, color: banner.text_color }"
      >
        <div class="dashboard-banner-icon">
          <feather-icon
            :icon="banner.icon"
            size="24"
          />
        </div>
        <div class="dashboard-banner-content">
          <strong>{{ banner.title }}</strong>
          <span v-if="banner.message">{{ banner.message }}</span>
        </div>
      </div>
    </div>

    <!-- Config mode: used on the Parameters page -->
    <b-card
      v-if="mode === 'config'"
      id="dashboard-banners"
      class="config-card"
    >
      <template #header>
        <div class="card-header-custom">
          <i class="fas fa-bullhorn icon" />
          <h4 class="mb-0">
            Bannières du tableau de bord
          </h4>
        </div>
      </template>

      <div class="config-content">
        <p class="config-description">
          Ajoutez des bannières visibles sur la page d'accueil. Chaque bannière peut avoir une icône, une couleur et être activée ou désactivée.
        </p>

        <div class="banner-form">
          <b-row>
            <b-col md="4">
              <b-form-group
                label="Titre"
                class="form-group-enhanced"
              >
                <b-form-input
                  v-model.trim="newBanner.title"
                  placeholder="Ex: Information importante"
                  class="form-input-enhanced"
                />
              </b-form-group>
            </b-col>
            <b-col md="8">
              <b-form-group
                label="Message"
                class="form-group-enhanced"
              >
                <b-form-textarea
                  v-model.trim="newBanner.message"
                  rows="2"
                  max-rows="4"
                  placeholder="Texte de la bannière"
                  class="form-input-enhanced textarea-input"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="4">
              <b-form-group
                label="Icône"
                class="form-group-enhanced"
              >
                <b-form-select
                  v-model="newBanner.icon"
                  :options="bannerIconOptions"
                  class="form-input-enhanced"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group
                label="Couleur de fond"
                class="form-group-enhanced"
              >
                <b-form-input
                  v-model="newBanner.background_color"
                  type="color"
                  class="color-input"
                />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group
                label="Couleur du texte"
                class="form-group-enhanced"
              >
                <b-form-input
                  v-model="newBanner.text_color"
                  type="color"
                  class="color-input"
                />
              </b-form-group>
            </b-col>
            <b-col
              md="2"
              class="d-flex align-items-center"
            >
              <b-button
                variant="primary"
                class="save-btn banner-add-btn"
                :disabled="!newBanner.title"
                @click="addBanner"
              >
                <i class="fas fa-plus mr-1" />
                Ajouter
              </b-button>
            </b-col>
          </b-row>
        </div>

        <div
          v-if="banners.length"
          class="banner-list"
        >
          <div
            v-for="banner in banners"
            :key="banner.id"
            class="banner-config-item"
          >
            <div
              class="banner-preview"
              :style="{ backgroundColor: banner.background_color, color: banner.text_color }"
            >
              <feather-icon
                :icon="banner.icon"
                size="22"
                class="mr-2"
              />
              <div class="banner-preview-text">
                <strong>{{ banner.title }}</strong>
                <span>{{ banner.message }}</span>
              </div>
            </div>
            <div class="banner-config-actions">
              <b-form-checkbox
                v-model="banner.is_active"
                switch
              >
                {{ banner.is_active ? 'Active' : 'Inactive' }}
              </b-form-checkbox>
              <b-button
                variant="outline-danger"
                size="sm"
                @click="deleteBanner(banner.id)"
              >
                <i class="fas fa-trash mr-1" />
                Supprimer
              </b-button>
            </div>
          </div>
        </div>
        <div
          v-else
          class="empty-banner-state"
        >
          Aucune bannière configurée.
        </div>

        <div class="action-buttons">
          <b-button
            variant="primary"
            size="lg"
            :disabled="loading"
            class="save-btn"
            @click="saveBanners"
          >
            <b-spinner
              v-if="loading"
              small
              class="mr-2"
            />
            <i
              v-else
              class="fas fa-save mr-2"
            />
            Enregistrer les bannières
          </b-button>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BCard,
  BButton,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BFormCheckbox,
  BSpinner,
  BRow,
  BCol,
} from 'bootstrap-vue'

export default {
  name: 'DashboardBanners',
  components: {
    BCard,
    BButton,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BFormCheckbox,
    BSpinner,
    BRow,
    BCol,
  },
  props: {
    mode: {
      type: String,
      default: 'display',
      validator: value => ['display', 'config'].includes(value),
    },
  },
  data() {
    return {
      banners: [],
      loading: false,
      newBanner: {
        title: '',
        message: '',
        icon: 'InfoIcon',
        background_color: '#e30613',
        text_color: '#ffffff',
        is_active: true,
      },
      bannerIconOptions: [
        { text: 'Information', value: 'InfoIcon' },
        { text: 'Alerte', value: 'AlertTriangleIcon' },
        { text: 'Annonce', value: 'BellIcon' },
        { text: 'Succès', value: 'CheckCircleIcon' },
        { text: 'Livraison', value: 'TruckIcon' },
        { text: 'Colis', value: 'PackageIcon' },
        { text: 'Calendrier', value: 'CalendarIcon' },
        { text: 'Maintenance', value: 'ToolIcon' },
      ],
    }
  },
  computed: {
    activeBanners() {
      return (this.banners || []).filter(banner => banner.is_active)
    },
  },
  created() {
    this.fetchBanners()
  },
  methods: {
    async fetchBanners() {
      try {
        const { data } = await axios.get('/api/core/parameters/dashboard-banners/config/')
        this.banners = Array.isArray(data.banners) ? data.banners : []
      } catch (error) {
        this.banners = []
      }
    },
    addBanner() {
      if (!this.newBanner.title) return
      this.banners.push({
        ...this.newBanner,
        id: `local-${Date.now()}`,
      })
      this.newBanner = {
        title: '',
        message: '',
        icon: 'InfoIcon',
        background_color: '#e30613',
        text_color: '#ffffff',
        is_active: true,
      }
    },
    deleteBanner(id) {
      this.banners = this.banners.filter(banner => banner.id !== id)
    },
    async saveBanners() {
      this.loading = true
      try {
        const { data } = await axios.put('/api/core/parameters/dashboard-banners/config/', {
          banners: this.banners,
        })
        this.banners = Array.isArray(data.banners) ? data.banners : []
        this.$bvToast.toast('Bannières enregistrées avec succès', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.error || "Erreur lors de l'enregistrement des bannières", {
          title: 'Erreur',
          variant: 'danger',
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.dashboard-banners {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.dashboard-banner {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.dashboard-banner-icon {
  margin-right: 1rem;
  flex-shrink: 0;
}

.dashboard-banner-content {
  display: flex;
  flex-direction: column;
}

.dashboard-banner-content strong {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.dashboard-banner-content span {
  font-size: 0.875rem;
  opacity: 0.95;
}

.banner-form {
  margin-bottom: 1.5rem;
}

.banner-list {
  display: grid;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.banner-config-item {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem;
  background: #fff;
}

.banner-preview {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 0.75rem;
}

.banner-preview-text {
  display: flex;
  flex-direction: column;
}

.banner-config-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.empty-banner-state {
  text-align: center;
  padding: 2rem;
  color: #718096;
  background: #f7fafc;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}
</style>
